// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { connect, ConnectedProps } from 'react-redux';
import { OrderedListOutlined } from '@ant-design/icons';
import styles from './styles/stepStyles';
import { runwayColors } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Layout from 'antd/lib/layout/layout';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import {
    selectAppSettingAutomations,
    selectRelease,
    selectReleaseStep,
    selectStepRun,
    selectFiltersAndFixesCount,
    selectAllReleaseBranchWorkItems,
    selectAllWorkingBranchWorkItems,
    selectSearch,
} from '../selectors/selectors';
import FeatureReadinessItems from './featureReadiness/FeatureReadinessItems';
import CherryPickQueueDrawer from './featureReadiness/CherryPickQueueDrawer';
import { AutomationType, FeatureItem, ReleaseStepFeatureReadinessData, ReleaseTimelinePhase } from '../types';
import PercentCompleteComponent from './featureReadiness/PercentCompleteComponent';
import { FormInstance } from 'antd/lib/form';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { getAutomationIdByType, getFeatureItemsIgnoredCount } from '@/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import {
    selectFilteredSortedDoneItemsForStepRun,
    selectFilteredSortedPendingWorkItemsForStepRun,
} from '@/selectors/memoizedSelectors';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface State {
    workingBranchEnabled?: boolean;
    appliedFiltersCount: number;
    isCherryPickQueueOpen: boolean;
    highlightedQueueItem: FeatureItem | null;
}

class StepContentFeatureReadiness extends React.Component<Props, State> {
    filtersForm: React.RefObject<FormInstance>;

    constructor(props) {
        super(props);

        this.state = {
            workingBranchEnabled: null,
            appliedFiltersCount: this.props.appliedFiltersCount,
            isCherryPickQueueOpen: false,
            highlightedQueueItem: null,
        };
    }

    openQueue = (highlightedQueueItem: FeatureItem | null) => {
        this.setState({ isCherryPickQueueOpen: true, highlightedQueueItem });
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        if (!props.showBranchSelectionField || state.workingBranchEnabled !== null) {
            return null;
        }

        if (!props.releaseBranchExists) {
            // default to showing items from working branch when release branch doesn't exist

            return {
                ...state,
                workingBranchEnabled: true,
            };
        }

        if (props.linkedItemOnWorkingBranchOnly !== null) {
            return {
                ...state,
                workingBranchEnabled: props.linkedItemOnWorkingBranchOnly,
            };
        }

        return null;
    }

    render() {
        return (
            <Layout
                style={{
                    ...styles.innerContent,
                    ...styles.spaceBetween,
                    minWidth: 1000,
                    paddingTop: 0,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        flexShrink: 0,
                        paddingBottom: 65,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: 40,
                            paddingBottom: 12,
                        }}
                    >
                        <div style={{ marginRight: 36 }}>
                            <PercentCompleteComponent
                                workingBranchEnabled={this.state.workingBranchEnabled}
                                percentComplete={
                                    this.state.workingBranchEnabled ?
                                        this.props.workingBranchPercentComplete
                                    :   this.props.percentComplete
                                }
                                appliedFiltersCount={this.props.appliedFiltersCount}
                            />
                        </div>
                        <Space direction="vertical">
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                {this.props.showBranchSelectionField && (
                                    <Space direction="vertical" style={{ marginRight: 24 }}>
                                        <Space>
                                            <Text strong>Base branch</Text>
                                            <TooltipInfoIcon tooltipContent="View progress of items of work against the selected branch" />
                                        </Space>
                                        <Space>
                                            <Select
                                                onChange={(value) => {
                                                    const workingBranchEnabled = value === this.props.workingBranch;

                                                    this.setState({
                                                        ...this.state,
                                                        workingBranchEnabled,
                                                    });
                                                }}
                                                value={
                                                    this.state.workingBranchEnabled ?
                                                        this.props.workingBranch
                                                    :   this.props.releaseBranch
                                                }
                                                style={{ minWidth: 300 }}
                                                disabled={this.props.disableBranchSelectionField}
                                            >
                                                <Select.Option
                                                    value={this.props.releaseBranch}
                                                    disabled={!this.props.releaseBranchExists}
                                                >
                                                    {this.props.releaseBranch}
                                                    {!this.props.releaseBranchExists && ' (not created)'}
                                                </Select.Option>
                                                <Select.Option value={this.props.workingBranch}>
                                                    {this.props.workingBranch}
                                                </Select.Option>
                                            </Select>
                                        </Space>
                                    </Space>
                                )}
                            </div>
                            {this.state.workingBranchEnabled && (
                                <Text style={{ color: runwayColors.statusYellow }}>
                                    {this.props.releaseBranchExists ?
                                        'Release branch not selected'
                                    :   'Release branch not created'}
                                </Text>
                            )}
                            <Button
                                type="link"
                                icon={<OrderedListOutlined />}
                                onClick={() => this.openQueue(null)}
                                style={{ padding: 0, marginTop: 8 }}
                            >
                                View cherry-pick and backmerge queue
                            </Button>
                        </Space>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                paddingLeft: 24,
                                flexGrow: 1,
                            }}
                        ></div>
                        <div style={{ marginTop: '-40px' }}>
                            <ReleaseEventsTimelineSection
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                stepRunId={this.props.stepRunComponentProps?.stepRunId}
                                context={ReleaseTimelineSectionContext.ReleaseStep}
                                releaseStepType={this.props.releaseStepType}
                            />
                        </div>
                    </div>
                    <FeatureReadinessItems
                        releaseStepType={this.props.releaseStepType}
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        releaseId={this.props.releaseId}
                        stepRunId={this.props.stepRunId}
                        releaseType={this.props.releaseType}
                        workingBranchEnabled={
                            this.state.workingBranchEnabled === null ? false : this.state.workingBranchEnabled
                        }
                        percentComplete={
                            this.state.workingBranchEnabled ?
                                this.props.workingBranchPercentComplete
                            :   this.props.percentComplete
                        }
                        userId={this.props.userId}
                        latestStagingBuild={this.props.latestStagingBuild}
                        selectedStagingBuild={this.props.selectedStagingBuild}
                        latestWorkingBranchBuild={this.props.latestWorkingBranchBuild}
                        appStoreSelectedBuilds={this.props.appStoreSelectedBuilds}
                        autoCherryPickAutomationId={this.props.autoCherryPickAutomationId}
                        autoMergeRunwayPRsAutomationId={this.props.autoMergeRunwayPRsAutomationId}
                        autoDownloadArtifactsAutomationId={this.props.autoDownloadArtifactsAutomationId}
                        autoUploadDsymsToStabilityMonitoringAutomationId={
                            this.props.autoUploadDsymsToStabilityMonitoringAutomationId
                        }
                        autoUploadBinaryArtifactToAppStoreAutomationId={
                            this.props.autoUploadBinaryArtifactToAppStoreAutomationId
                        }
                        isAutoMergeRunwayPRsEnabled={this.props.isAutoMergeRunwayPRsEnabled}
                        isReleaseKickedOff={this.props.isReleaseKickedOff}
                        isReleaseSubmitted={this.props.isReleaseSubmitted}
                        requiresFixRequestsApprovals={this.props.requiresFixRequestsApprovals}
                        releaseStatus={this.props.releaseStatus}
                        stepRunComponentProps={this.props.stepRunComponentProps}
                        onOpenQueue={this.openQueue}
                        openBackmergePRs={this.props.openBackmergePRs}
                    />
                </div>
                <CherryPickQueueDrawer
                    appId={this.props.appId}
                    stepRunId={this.props.stepRunId}
                    open={this.state.isCherryPickQueueOpen}
                    onClose={() => this.setState({ isCherryPickQueueOpen: false, highlightedQueueItem: null })}
                    highlightedItem={this.state.highlightedQueueItem}
                    openBackmergePRs={this.props.openBackmergePRs}
                    isAutoMergeRunwayPRsEnabled={this.props.isAutoMergeRunwayPRsEnabled}
                />
            </Layout>
        );
    }
}

const mapDispatch = (_) => {
    return {};
};

const mapProps = (
    state: RootState,
    ownProps: ReleaseStepOrStepRunComponentProps,
    initialSearchParams: URLSearchParams,
) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const release = selectRelease(state, releaseId);
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);
    const wfStepRunId = ownProps.stepRunComponentProps?.stepRunId;
    const stepRun = wfStepRunId ? selectStepRun(state, wfStepRunId) : null;
    const backwardsCompatibleStepRunId = generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStepType);
    const stepRunId = wfStepRunId ?? releaseStep?.id ?? backwardsCompatibleStepRunId;
    const automationRuns = stepRun?.automationRuns ?? releaseStep?.automationRuns ?? [];
    const stepData = (
        stepRun?.data?.summaryData ?
            { ...stepRun.data.summaryData, ...stepRun.data.complementaryData }
        :   (releaseStep?.data ?? {})) as ReleaseStepFeatureReadinessData;

    const workingBranch = stepData.workingBranch;
    const userId = state.user.id;
    const releaseBranch = stepData.releaseBranch;
    const releaseBranchExists = stepData.doesBranchExist;

    const showBranchSelectionField =
        (releaseBranchExists && releaseBranch !== workingBranch) ||
        (!!workingBranch &&
            workingBranch !== releaseBranch &&
            (release.timelinePhase === ReleaseTimelinePhase.Current ||
                (release.timelinePhase === ReleaseTimelinePhase.Upcoming && stepData.workingBranchItems?.length > 0)));

    const appliedFiltersCount = selectFiltersAndFixesCount(state);

    const linkedItemId = initialSearchParams.get('selectedItem');
    const linkedFixRequestId = initialSearchParams.get('selectedFix');
    const isLinkedItem = (item: FeatureItem) =>
        (!!linkedItemId && item.id === linkedItemId) ||
        (!!linkedFixRequestId && item.fixRequest?.id === linkedFixRequestId);
    const linkedQuery = initialSearchParams.get('query');
    const hasLinkedQueryMatches = (workingBranch: boolean) =>
        !!linkedQuery &&
        selectSearch(state) === linkedQuery &&
        (selectFilteredSortedPendingWorkItemsForStepRun(state, stepRunId, workingBranch).length > 0 ||
            selectFilteredSortedDoneItemsForStepRun(state, stepRunId, workingBranch).length > 0);
    const linkedItemOnWorkingBranchOnly =
        selectAllReleaseBranchWorkItems(state, stepRunId).some(isLinkedItem) ? false
        : selectAllWorkingBranchWorkItems(state, stepRunId).some(isLinkedItem) ? true
        : hasLinkedQueryMatches(false) ? false
        : hasLinkedQueryMatches(true) ? true
        : null;
    const calculatePercentComplete = (workingBranch: boolean) => {
        const filteredSortedPendingItems = selectFilteredSortedPendingWorkItemsForStepRun(
            state,
            stepRunId,
            workingBranch,
        );
        const filteredSortedDoneItems = selectFilteredSortedDoneItemsForStepRun(state, stepRunId, workingBranch);

        const doneItemsIgnoredCount = getFeatureItemsIgnoredCount(filteredSortedDoneItems);
        const pendingItemsIgnoredCount = getFeatureItemsIgnoredCount(filteredSortedPendingItems);
        const doneCountNotIgnored = filteredSortedDoneItems.length - doneItemsIgnoredCount;
        const itemsCountNotIgnored =
            filteredSortedDoneItems.length +
            filteredSortedPendingItems.length -
            (pendingItemsIgnoredCount + doneItemsIgnoredCount);

        if (itemsCountNotIgnored > 0) {
            const percentage = (100 * doneCountNotIgnored) / itemsCountNotIgnored;

            // Special handling to avoid rounding to 100 misleadingly
            if (percentage > 99 && percentage < 100) {
                return 99;
            }

            return Math.round(percentage);
        }

        return 0;
    };

    const disableBranchSelectionField = !(
        release.timelinePhase === ReleaseTimelinePhase.Current ||
        (release.timelinePhase === ReleaseTimelinePhase.Upcoming && stepData.workingBranchItems?.length > 0)
    );
    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    const autoCherryPickAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoCherryPickFromWorkingBranch,
    );

    const automations = selectAppSettingAutomations(state, appId);

    // TODO: #WORKFLOWS# this is a temporary fix, what is the best way to get automation ids that apply to multiple steps?
    const autoMergeRunwayPRsAutomationId = automations.find(
        (automation) => automation.type === AutomationType.AutoMergeRunwayPRs,
    )?.id;

    return {
        orgId,
        appId,
        userId,
        stepRunId,
        releaseType: release.type,
        releaseId,
        stepRunComponentProps: ownProps.stepRunComponentProps,
        latestStagingBuild: stepData.latestStagingBuild,
        selectedStagingBuild: stepData.selectedStagingBuild,
        latestWorkingBranchBuild: stepData.latestWorkingBranchBuild,
        appStoreSelectedBuilds: stepData.appStoreSelectedBuilds,
        autoDownloadArtifactsAutomationId,
        autoUploadDsymsToStabilityMonitoringAutomationId,
        autoUploadBinaryArtifactToAppStoreAutomationId,
        autoCherryPickAutomationId,
        autoMergeRunwayPRsAutomationId,
        isAutoMergeRunwayPRsEnabled: stepData.isAutoMergeRunwayPRsEnabled,
        isReleaseKickedOff: stepData.isReleaseKickedOff,
        isReleaseSubmitted: stepData.isReleaseSubmitted,
        requiresFixRequestsApprovals: stepData.requiresFixRequestsApprovals,
        releaseStatus: stepData.releaseStatus,
        releaseStepType,
        workingBranch,
        releaseBranch,
        percentComplete: calculatePercentComplete(false),
        workingBranchPercentComplete: calculatePercentComplete(true),
        releaseBranchExists,
        showBranchSelectionField,
        disableBranchSelectionField,
        linkedItemOnWorkingBranchOnly,
        appliedFiltersCount,
        openBackmergePRs: stepData.openBackmergePRs,
    };
};

const makeMapProps = () => {
    const initialSearchParams = new URLSearchParams(window.location.search);
    return (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) =>
        mapProps(state, ownProps, initialSearchParams);
};

const connector = connect(makeMapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentFeatureReadiness);
